import { EmployeeItem } from "@/components/employee/EmployeeItem.tsx";
import { ResultFiles } from "@/components/form-result/result-modal/components/FileElementResult.tsx";
import useFormatter from "@/hooks/use-formatter.hook.ts";
import {
  EmptyStateBase,
  Modal,
  ModalProps,
  Box,
  Button,
  EmojiDisplay,
  HtmlViewer,
  Skeleton,
  Stack,
  Text,
  Title,
} from "@skailer-com/uikit";
import { linkifyText } from "@/helpers/utils.helper.ts";
import { useSurveyQuestionResultsSearchInfinite } from "@/queries/surveys/use-surveys.query.ts";
import analyticsService, {
  analyticEvents,
  analyticProperties,
} from "@/services/analytics-service.ts";
import { ApiDepartmentShort } from "@/types/department.types.ts";
import { ApiEmployeeShort } from "@/types/employees/employee.types.ts";
import { ApiFormElementAggregation } from "@/types/forms/form-result.types.ts";
import { ApiFormViewElement } from "@/types/forms/form-view.types.ts";
import { ApiFormElementType } from "@/types/forms/form.types.ts";
import {
  ApiSurveyAggregatedResultsFilter,
  ApiSurveyResultSearchItem,
  ApiSurveyResultsFilter,
} from "@/types/survey.types.ts";
import { ReactElement, useCallback, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useEffectOnce } from "react-use";

interface SurveyQuestionResultsModalProps extends ModalProps {
  surveyId: number;
  element: ApiFormViewElement;
  filterResult?: ApiSurveyAggregatedResultsFilter;
  answerScaleOptionIds?: number[];
  answerSelectOptionIds?: number[];
  selectableOptionIds?: number[];
  resultAggregation?: ApiFormElementAggregation | null;
  managers?: ApiEmployeeShort[];
  departments?: ApiDepartmentShort[];
}

export function SurveyQuestionResultsModal({
  surveyId,
  element,
  filterResult,
  answerScaleOptionIds,
  answerSelectOptionIds,
  selectableOptionIds,
  resultAggregation,
  managers,
  departments,
  ...props
}: SurveyQuestionResultsModalProps) {
  const { t } = useTranslation();
  const { renderEmployeeName } = useFormatter();
  const tableContainerRef = useRef<HTMLDivElement>(null);

  const getInitialFilterOptionIds = (): number[] | null => {
    switch (element.type) {
      case ApiFormElementType.scale:
        return answerScaleOptionIds ?? null;
      case ApiFormElementType.select:
        return answerSelectOptionIds ?? null;
      case ApiFormElementType.employee_nps:
        return selectableOptionIds ?? null;
      default:
        return null;
    }
  };

  const [selectedFilterOptionIds, setSelectedFilterOptionIds] = useState<number[] | null>(
    getInitialFilterOptionIds
  );

  const filterOptions = useMemo(() => {
    switch (element.type) {
      case ApiFormElementType.scale:
        return (
          element.scale_settings?.form_scale.scale
            .map((option) => ({
              id: option.id,
              title: option.title,
              emoji: option.emoji,
            }))
            .sort((a, b) => a.id - b.id) ?? []
        );
      case ApiFormElementType.select:
        return (
          element.select_settings?.options.map((option) => ({
            id: option.id,
            title: option.title,
            emoji: option.emoji,
          })) ?? []
        );
      case ApiFormElementType.employee_nps:
        return Array.from({ length: 11 }, (_, i) => ({
          id: i,
          title: String(i),
          emoji: null,
        }));
      default:
        return [];
    }
  }, [element]);

  const getOptionPercent = useCallback(
    (optionId: number): number | null => {
      if (!resultAggregation) return null;

      let options: { selectable_option_id: number; count: number }[] | undefined;

      switch (element.type) {
        case ApiFormElementType.scale:
          options = resultAggregation.scale_data?.options;
          break;
        case ApiFormElementType.select:
          options = resultAggregation.select_data?.options;
          break;
        case ApiFormElementType.employee_nps:
          options = resultAggregation.employee_nps_data?.options;
          break;
      }

      if (!options) return null;

      const total = options.reduce((sum, opt) => sum + opt.count, 0);
      if (total === 0) return null;

      const option = options.find((opt) => opt.selectable_option_id === optionId);
      if (!option) return 0;

      return Math.round((option.count / total) * 100);
    },
    [resultAggregation, element.type]
  );

  const getFilterCaption = useMemo(() => {
    if (managers && managers.length > 0) {
      const managerNames = managers.map((m) => renderEmployeeName(m)).join(", ");
      return managers.length === 1
        ? t("surveys:filtered_by_manager", { name: managerNames })
        : t("surveys:filtered_by_managers", { names: managerNames });
    }
    if (departments && departments.length > 0) {
      const departmentNames = departments.map((d) => d.title).join(", ");
      return departments.length === 1
        ? t("surveys:filtered_by_department", { name: departmentNames })
        : t("surveys:filtered_by_departments", { names: departmentNames });
    }
    return undefined;
  }, [managers, departments]);

  const filter = useMemo<ApiSurveyResultsFilter>(() => {
    const baseFilter: ApiSurveyResultsFilter = {
      survey_id: surveyId,
      form_element_id: element.id,
      page_number: 1,
      ...filterResult,
      selectable_option_ids:
        element.type === ApiFormElementType.employee_nps ? undefined : selectableOptionIds,
      manager_ids: managers?.map((m) => m.id),
      department_ids: departments?.map((d) => d.id),
    };

    if (selectedFilterOptionIds !== null) {
      switch (element.type) {
        case ApiFormElementType.scale:
          baseFilter.answer_scale_option_ids = selectedFilterOptionIds;
          break;
        case ApiFormElementType.select:
          baseFilter.answer_select_option_ids = selectedFilterOptionIds;
          break;
        case ApiFormElementType.employee_nps:
          baseFilter.selectable_option_ids = selectedFilterOptionIds;
          break;
      }
    }

    return baseFilter;
  }, [
    surveyId,
    element.id,
    element.type,
    filterResult,
    selectedFilterOptionIds,
    managers,
    departments,
    selectableOptionIds,
  ]);

  const {
    data: results,
    isLoading,
    isFetching,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
  } = useSurveyQuestionResultsSearchInfinite(filter);

  const flatData = useMemo(
    () => results?.pages?.flatMap((page) => page?.items ?? []) ?? [],
    [results]
  );
  // Ниже порога анонимности бэк отдаёт пустую страницу с признаком — это не отсутствие ответов
  const isBelowAnonymityThreshold = results?.pages?.[0]?.is_below_anonymity_threshold ?? false;
  const fetchMoreOnBottomReached = useCallback(
    (scrollHeight: number, scrollTop: number, clientHeight: number) => {
      if (
        scrollHeight - scrollTop - clientHeight < 400 &&
        !isFetching &&
        hasNextPage &&
        !isFetchingNextPage
      ) {
        fetchNextPage();
      }
    },
    [fetchNextPage, isFetching, hasNextPage, isFetchingNextPage]
  );

  useEffectOnce(() => {
    analyticsService.trackEvent(analyticEvents.surveys.questionResultViewed, {
      [analyticProperties.id]: element.id,
    });
  });

  const getTitle = () => {
    let title: string | null = null;
    let subtitle: string | null;
    switch (element.type) {
      case ApiFormElementType.text:
        title = element.text_settings?.title || null;
        break;
      case ApiFormElementType.open_answer:
        title = element.open_answer_settings?.title || null;
        break;
      case ApiFormElementType.scale:
        title = element.scale_settings?.title || null;
        break;
      case ApiFormElementType.select:
        title = element.select_settings?.title || null;
        break;
      case ApiFormElementType.employee_nps:
        title =
          element.employee_nps_settings?.title ?? t("surveys:employee_nps_element_title_default");
        break;
      case ApiFormElementType.file:
        title = element.file_settings?.title ?? t("surveys:question_type_file");
        break;
    }

    if (isBelowAnonymityThreshold) {
      subtitle = null;
    } else if (results && results.pages.length > 0) {
      const totalAnswers = results.pages[0]?.metadata?.total_items;
      if (totalAnswers) {
        subtitle = t("plurals:people_answered", { count: totalAnswers });
      } else {
        subtitle = t("performance_review:no_answers_yet");
      }
    } else {
      subtitle = `${t("common:loading")}...`;
    }

    return (
      <Title
        header={title || "-"}
        caption={subtitle || undefined}
        size={"lg"}
        paddingBottom={true}
      />
    );
  };

  const getAnswer = (answer: ApiSurveyResultSearchItem) => {
    if (!answer.answer) return <Text>-</Text>;

    switch (element.type) {
      case ApiFormElementType.open_answer:
        return (
          <Box className={"overflow-hidden"}>
            <HtmlViewer content={linkifyText(answer.answer.answer_text ?? "")} />
          </Box>
        );
      case ApiFormElementType.employee_nps:
        return <Text>{answer.answer.answer_int}</Text>;
      case ApiFormElementType.file:
        return <ResultFiles files={answer.answer.answer_files ?? []} />;
      case ApiFormElementType.select: {
        const selectSettings = element.select_settings;
        if (!selectSettings || !answer.answer.answer_select_option_ids) return null;

        const selectedOptions = selectSettings.options.filter((option) =>
          answer.answer?.answer_select_option_ids?.includes(option.id)
        );
        const selectResult: ReactElement[] = [];
        for (const selectedOption of selectedOptions) {
          if (selectedOption.emoji) {
            selectResult.push(
              <EmojiDisplay
                key={`emoji-${selectedOption.id}`}
                size={"md"}
                id={selectedOption.emoji}
              />
            );
          } else if (selectedOption.title) {
            selectResult.push(
              <Text key={`title-${selectedOption.id}`}>{selectedOption.title}</Text>
            );
          }
        }

        return selectResult;
      }
      case ApiFormElementType.scale: {
        const scaleSettings = element.scale_settings;
        if (!scaleSettings || !answer.answer.answer_scale_option_ids) return null;

        const selectedOptions = scaleSettings.form_scale.scale
          .filter((option) => answer.answer?.answer_scale_option_ids?.includes(option.id))
          .sort((a, b) => a.sort - b.sort);
        const selectResult: ReactElement[] = [];
        for (const selectedOption of selectedOptions) {
          if (selectedOption.emoji) {
            selectResult.push(
              <EmojiDisplay
                key={`emoji-${selectedOption.id}`}
                size={"md"}
                id={selectedOption.emoji}
              />
            );
          } else if (selectedOption.title) {
            selectResult.push(
              <Text key={`title-${selectedOption.id}`}>{selectedOption.title}</Text>
            );
          }
        }
        if (selectResult.length === 0) {
          return null;
        }

        return selectResult;
      }
    }
  };

  return (
    <Modal
      title={t("surveys:answers_breakdown")}
      caption={getFilterCaption}
      {...props}
      layout="stretch"
      size={"lg"}
      withCloser={true}
      onModalContentScroll={(scrollTop, scrollHeight, height) => {
        fetchMoreOnBottomReached(scrollHeight, scrollTop, height);
      }}
    >
      {getTitle()}

      {filterOptions.length > 0 && !managers?.length && !departments?.length ? (
        <Stack direction={"horizontal"} gap={"sm"} className={"flex-wrap mb-4"}>
          <Button
            variant={selectedFilterOptionIds === null ? "primary" : "secondary"}
            onClick={() => setSelectedFilterOptionIds(null)}
          >
            {t("common:all")}
          </Button>
          {filterOptions.map((option) => {
            const percent = getOptionPercent(option.id);
            const isSelected = selectedFilterOptionIds?.includes(option.id) ?? false;
            return (
              <Button
                key={option.id}
                variant={isSelected ? "primary" : "secondary"}
                onClick={() => {
                  setSelectedFilterOptionIds((prev) => {
                    if (prev === null) return [option.id];
                    if (isSelected) {
                      const newIds = prev.filter((id) => id !== option.id);
                      return newIds.length > 0 ? newIds : null;
                    }
                    return [...prev, option.id];
                  });
                }}
              >
                <Stack direction={"horizontal"} items={"center"} gap={"xs"}>
                  {option.emoji ? <EmojiDisplay size={"sm"} id={option.emoji} /> : option.title}
                  <Text className={"font-normal"}>{percent !== null && ` — ${percent}%`}</Text>
                </Stack>
              </Button>
            );
          })}
        </Stack>
      ) : (
        <></>
      )}

      <Box ref={tableContainerRef} className={"overflow-y-auto h-full"}>
        <Stack gap={"md"}>
          {flatData.map((row, index) => (
            <Box variant={"border"} key={index}>
              <Stack gap={"sm"}>
                {row.employee ? (
                  <EmployeeItem
                    employee={row.employee}
                    direction={"horizontal"}
                    size={"sm"}
                    variant={"secondary"}
                  />
                ) : (
                  <Text className={"text-secondary"}>{t("common:anonymous")}</Text>
                )}
                <Box>{getAnswer(row)}</Box>
                {row.answer?.comment && (
                  <Text className={"whitespace-pre-wrap text-secondary"}>{row.answer.comment}</Text>
                )}
              </Stack>
            </Box>
          ))}
        </Stack>
        {(isLoading || isFetching) && <Skeleton className={"h-10 w-full"} />}
        {!isLoading && !isFetching && flatData.length === 0 && isBelowAnonymityThreshold && (
          <EmptyStateBase
            emoji={":lock:"}
            title={t("surveys:result_not_available_yet_title")}
            description={t("surveys:result_not_available_yet_description")}
          />
        )}
        {!isLoading && !isFetching && flatData.length === 0 && !isBelowAnonymityThreshold && (
          <EmptyStateBase title={t("performance_review:no_answers_yet")} emoji={":mag:"} />
        )}
      </Box>
    </Modal>
  );
}
